import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Snowflake } from "lucide-react";
import { api } from "@/lib/api";

export const CLS_STYLE = {
  CORRECT: "bg-emerald-100 text-emerald-800",
  PARSER_MISSED: "bg-red-100 text-red-800",
  PARSER_WRONG: "bg-rose-200 text-rose-900",
  FALSE_POSITIVE: "bg-orange-100 text-orange-800",
  DUPLICATED_ACROSS_VIEWS: "bg-purple-100 text-purple-800",
  AMBIGUOUS: "bg-amber-100 text-amber-800",
  SOURCE_MISSING: "bg-zinc-200 text-zinc-700",
  REVIEW_REQUIRED: "bg-sky-100 text-sky-800",
  NOT_APPLICABLE: "bg-zinc-100 text-zinc-500",
  EXTRACTED: "bg-emerald-100 text-emerald-800",
  MISSING: "bg-zinc-200 text-zinc-600",
  EXPLICIT: "bg-emerald-100 text-emerald-800",
  GEOMETRICALLY_DETECTED: "bg-sky-100 text-sky-800",
  CALCULATED: "bg-indigo-100 text-indigo-800",
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.map(fmt).join(" × ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const short = (h) => (h ? `${h.slice(0, 10)}…${h.slice(-6)}` : "—");

/* ── Meta / audit ─────────────────────────────────────────────── */
const MetaBar = ({ meta, audit }) => (
  <div className="text-[11px] grid md:grid-cols-2 gap-x-6 gap-y-0.5 border border-border p-2 bg-zinc-50" data-testid="baseline-meta">
    <div>Motore: schema <b>{meta.engine.pdf_schema_version}</b> · deepread <b>{meta.engine.deepread_version}</b> · KB <b>{meta.engine.knowledge_version || "—"}</b> · CMM <b>{meta.engine.cmm_version}</b> · planner <b>{meta.engine.planner_version}</b></div>
    <div>Revisione motore (git): <span className="font-mono" title={meta.engine.git_commit}>{short(meta.engine.git_commit)}</span></div>
    <div>Hash PDF: <span className="font-mono" title={meta.pdf_sha256_computed} data-testid="baseline-pdf-hash">{short(meta.pdf_sha256_computed)}</span> {meta.pdf_sha256_match ? "✓ combacia con l'inventario" : "✗ NON combacia"}</div>
    <div>Hash output grezzo: <span className="font-mono" title={meta.output_sha256} data-testid="baseline-output-hash">{short(meta.output_sha256)}</span></div>
    <div>Inizio: {meta.started_at} · fine: {meta.finished_at}</div>
    <div>Durata: <b>{meta.elapsed_s}s</b> · chiamate esterne: <b data-testid="baseline-external-calls">{meta.external_calls.total}</b>{meta.external_calls.vision_registry?.length > 0 && ` (vision: ${meta.external_calls.vision_registry.map((v) => v.model).join(", ")})`}</div>
    <div>Errori: {meta.errors?.length ? <span className="text-red-700">{meta.errors[0]}</span> : "0"} · warning: {meta.warnings?.length || 0}</div>
    <div data-testid="baseline-audit-order">
      {audit ? (
        <span className={audit.order_ok ? "text-emerald-700" : "text-red-700"}>
          Congelata {audit.baseline_frozen_at} → GT letta {audit.ground_truth_first_accessed_at} {audit.order_ok ? "✓ ordine dimostrato" : "✗ ORDINE VIOLATO"}
        </span>
      ) : (
        <span className="text-emerald-700 flex items-center gap-1"><Snowflake size={11} /> congelata {meta.frozen_at} (immutabile)</span>
      )}
    </div>
    {meta.warnings?.length > 0 && <div className="md:col-span-2 text-amber-800">Warning: {meta.warnings.join(" · ")}</div>}
  </div>
);

/* ── Output grezzo ────────────────────────────────────────────── */
const FeatTable = ({ rows, cols, testid }) => (
  rows?.length ? (
    <table className="w-full text-[11px]" data-testid={testid}>
      <thead><tr className="text-left text-muted-foreground">{cols.map(([k, l]) => <th key={k} className="pr-2 py-0.5">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t border-border">
          {cols.map(([k]) => <td key={k} className="pr-2 py-0.5">{k === "status" ? <Badge className={`text-[9px] ${CLS_STYLE[r[k]] || ""}`}>{fmt(r[k])}</Badge> : fmt(r[k])}</td>)}
        </tr>))}
      </tbody>
    </table>
  ) : <p className="text-[11px] text-muted-foreground">nessuna famiglia riconosciuta dal motore</p>
);

const RawView = ({ recognized, expert }) => {
  const r = recognized;
  return (
    <div className="space-y-3" data-testid="baseline-raw-view">
      <p className="text-[11px] text-amber-800 border border-amber-200 bg-amber-50 p-1.5">{r.nota_vuoti}</p>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
        {(r.campi || []).map((c) => (
          <div key={c.key} className="text-xs flex items-start gap-2 border-b border-border/60 py-0.5" data-testid={`raw-field-${c.key}`}>
            <span className="w-44 shrink-0 text-muted-foreground">{c.label}</span>
            <span className="font-medium break-all">{fmt(c.value)}{c.unit ? ` ${c.unit}` : ""}</span>
            <Badge className={`text-[9px] ml-auto shrink-0 ${CLS_STYLE[c.status] || "bg-zinc-100"}`}>{c.status}</Badge>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold mb-1">Fori — totale modello: {fmt(r.fori?.totale_modello)} · deepread: {fmt(r.fori?.totale_deepread)}</h4>
          <FeatTable testid="raw-holes-table" rows={r.fori?.famiglie_deepread} cols={[["feature_id", "ID"], ["diameter_mm", "Ø mm"], ["quantity", "Qty"], ["view", "Vista"], ["status", "Stato"]]} />
        </div>
        <div>
          <h4 className="text-xs font-semibold mb-1">Asole — totale modello: {fmt(r.asole?.totale_modello)} · deepread: {fmt(r.asole?.totale_deepread)}</h4>
          <FeatTable testid="raw-slots-table" rows={r.asole?.famiglie_deepread} cols={[["feature_id", "ID"], ["width_mm", "Largh."], ["length_mm", "Lungh."], ["quantity", "Qty"], ["view", "Vista"], ["status", "Stato"]]} />
        </div>
        <div>
          <h4 className="text-xs font-semibold mb-1">Pieghe — bend_count: {fmt(r.pieghe?.bend_count_modello)}</h4>
          <FeatTable testid="raw-bends-table" rows={r.pieghe?.features} cols={[["angle_deg", "Angolo"], ["radius_mm", "Raggio"], ["quantity", "Qty"], ["status", "Stato"]]} />
        </div>
        <div>
          <h4 className="text-xs font-semibold mb-1">Filettature / svasature / lamature</h4>
          <FeatTable testid="raw-threads-table" rows={[...(r.filettature || []), ...(r.svasature || []), ...(r.lamature || [])]} cols={[["feature_type", "Tipo"], ["designation", "Design."], ["quantity", "Qty"], ["status", "Stato"]]} />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-[11px]">
        {[["Contorno esterno", r.contorno_esterno, "raw-ext-contour"], ["Contorni interni", r.contorni_interni, "raw-int-contour"], ["Sviluppo piano", r.sviluppo_piano, "raw-flat-dev"]].map(([lbl, it, tid]) => (
          <div key={lbl} className="border border-border p-1.5" data-testid={tid}>
            <b>{lbl}</b>: {fmt(it?.value)} {it?.unit || "mm"} <Badge className={`text-[9px] ${CLS_STYLE[it?.status] || "bg-zinc-100"}`}>{it?.status}</Badge>
            {it?.missing_inputs?.length > 0 && <div className="text-amber-700">input mancanti: {it.missing_inputs.join(", ")}</div>}
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-[11px]">
        <div className="border border-border p-1.5" data-testid="raw-quote-stats">
          <b>Quote lette</b>: {r.quote?.totali_lette} totali · {r.quote?.attive} attive · {r.quote?.associate_a_bordi} associate a bordi · <span className="text-amber-800">{r.quote?.non_associate} non associate</span> · {r.quote?.angolari} angolari · {r.quote?.scartate} scartate
          {r.quote?.motivi_scarto?.length > 0 && <div className="text-muted-foreground">motivi scarto: {r.quote.motivi_scarto.join(", ")}</div>}
        </div>
        <div className="border border-border p-1.5" data-testid="raw-ops">
          <b>Lavorazioni nel modello</b>: {(r.lavorazioni || []).length ? r.lavorazioni.map((o, i) => <span key={i} className="mr-2">{o.process}{o.qty ? `×${o.qty}` : ""}{o.status ? ` (${o.status})` : ""}</span>) : "nessuna"}
        </div>
        <div className="border border-border p-1.5" data-testid="raw-welds">
          <b>Saldature</b>: welding_notes={fmt(r.saldature?.welding_notes)} · operazioni: {(r.saldature?.operazioni || []).length || 0}
        </div>
        <div className="border border-border p-1.5" data-testid="raw-treatments">
          <b>Trattamenti</b>: superficiali {fmt(r.trattamenti?.superficiali)} · termici {fmt(r.trattamenti?.termici)} · finiture {fmt(r.trattamenti?.finiture)}
        </div>
        <div className="border border-border p-1.5" data-testid="raw-tolerances">
          <b>Tolleranze</b>: dim. {fmt(r.tolleranze?.dimensionali)} · geom. {fmt(r.tolleranze?.geometriche)} · rugosità {fmt(r.tolleranze?.rugosita)}
        </div>
        <div className="border border-border p-1.5" data-testid="raw-multipliers">
          <b>Moltiplicatori (2x, 4x…)</b>: {(r.moltiplicatori || []).length ? r.moltiplicatori.map((m, i) => <span key={i} className="mr-2">{m.feature_id}×{m.qty}</span>) : "nessuno rilevato"}
        </div>
      </div>
      {(r.conflitti?.length > 0 || r.da_rivedere?.length > 0) && (
        <div className="border border-amber-300 bg-amber-50 p-1.5 text-[11px]" data-testid="raw-conflicts">
          <b>Conflitti</b>: {r.conflitti?.length || 0} · <b>da rivedere</b>: {(r.da_rivedere || []).map((x) => x.name).join(", ") || 0}
        </div>
      )}
      {expert?.explanation && (
        <details className="text-[11px]" data-testid="raw-explanation">
          <summary className="cursor-pointer font-semibold">Spiegazione Expert Engine · prezzo: {expert.price_status}</summary>
          <pre className="whitespace-pre-wrap font-sans mt-1 border border-border p-2">{expert.explanation}</pre>
        </details>
      )}
    </div>
  );
};

/* ── Ground truth (FASE A bloccata) ───────────────────────────── */
const GtView = ({ gt }) => (
  <div className="space-y-1" data-testid="baseline-gt-view">
    <p className="text-[11px] text-sky-800 border border-sky-200 bg-sky-50 p-1.5 flex items-center gap-1">
      <Lock size={11} /> Ground truth FASE A bloccata e immutabile — mai fornita al parser.
    </p>
    {Object.entries(gt || {}).map(([k, v]) => (
      <div key={k} className="text-xs flex gap-2 border-b border-border/60 py-0.5" data-testid={`gt-field-${k}`}>
        <span className="w-44 shrink-0 text-muted-foreground">{k}</span>
        <span className="whitespace-pre-wrap">{fmt(v)}</span>
      </div>
    ))}
  </div>
);

/* ── Confronto voce per voce ──────────────────────────────────── */
const CompView = ({ comp }) => (
  <div className="space-y-2" data-testid="baseline-comp-view">
    <div className="flex gap-1 flex-wrap">
      {comp.classificazioni_ufficiali.map((c) => <Badge key={c} className={`text-[9px] ${CLS_STYLE[c]}`}>{c}</Badge>)}
    </div>
    <table className="w-full text-[11px]">
      <thead><tr className="text-left text-muted-foreground">
        <th className="pr-2 py-1">Categoria</th><th className="pr-2">Voce</th><th className="pr-2">FASE A (tecnico)</th>
        <th className="pr-2">QuoteOS</th><th className="pr-2">Classificazione</th><th>Causa tecnica</th>
      </tr></thead>
      <tbody>
        {comp.items.map((it) => (
          <tr key={it.id} className="border-t border-border align-top" data-testid={`cmp-row-${it.id}`}>
            <td className="pr-2 py-1 whitespace-nowrap">{it.category}</td>
            <td className="pr-2 font-medium">{it.label}</td>
            <td className="pr-2 max-w-[180px]"><span title={fmt(it.gt_raw)}>{fmt(it.gt_parsed ?? it.gt_raw)}</span></td>
            <td className="pr-2 max-w-[180px] break-all">{fmt(it.quoteos_value)}</td>
            <td className="pr-2"><Badge className={`text-[9px] ${CLS_STYLE[it.classification]}`} data-testid={`cmp-cls-${it.id}`}>{it.classification}</Badge></td>
            <td className="text-muted-foreground max-w-[320px]">{it.technical_cause}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ── Metriche + tabella finale ────────────────────────────────── */
const MetricsView = ({ comp }) => {
  const m = comp.metriche?.per_categoria || {};
  return (
    <div className="space-y-3" data-testid="baseline-metrics-view">
      <table className="w-full text-[11px]" data-testid="category-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Categoria</th><th className="pr-2">Presente nel disegno</th><th className="pr-2">Riconosciuto</th>
          <th className="pr-2">Corretto</th><th className="pr-2">Mancato</th><th className="pr-2">Errato</th><th className="pr-2">Falsi pos.</th>
          <th className="pr-2">Duplicati</th><th className="pr-2">Ambigui</th><th className="pr-2">Review</th><th className="pr-2">Precision</th><th>Recall</th>
        </tr></thead>
        <tbody>
          {[...(comp.tabella_categorie || []), ...(comp.tabella_extra || [])].map((r) => (
            <tr key={r.categoria} className="border-t border-border" data-testid={`cat-row-${r.categoria}`}>
              <td className="pr-2 py-1 font-medium">{r.categoria}</td>
              <td className="pr-2">{r.presente_nel_disegno}</td><td className="pr-2">{r.riconosciuto}</td>
              <td className="pr-2 text-emerald-700">{r.corretti}</td><td className="pr-2 text-red-700">{r.mancati}</td>
              <td className="pr-2 text-rose-700">{r.errati}</td><td className="pr-2 text-orange-700">{r.falsi_positivi}</td>
              <td className="pr-2 text-purple-700">{r.duplicati}</td><td className="pr-2 text-amber-700">{r.ambigui}</td>
              <td className="pr-2 text-sky-700">{r.review}</td>
              <td className="pr-2">{m[r.categoria]?.precision ?? "n/d"}</td>
              <td>{m[r.categoria]?.recall ?? "n/d"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-muted-foreground">{comp.metriche?.nota}</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-sky-200 bg-sky-50 p-2" data-testid="blocked-ok-list">
          <h4 className="text-xs font-semibold text-sky-900">Dati incerti correttamente bloccati ({(comp.dati_bloccati_correttamente || []).length})</h4>
          {(comp.dati_bloccati_correttamente || []).map((it) => (
            <div key={it.id} className="text-[11px] py-0.5 border-b border-sky-100">{it.label}: {it.technical_cause}</div>
          ))}
        </div>
        <div className={`border p-2 ${comp.gate_preventivo?.bloccato ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`} data-testid="danger-list">
          <h4 className="text-xs font-semibold">Errori che rischierebbero di entrare nel preventivo ({(comp.errori_da_preventivo || []).length}) — gate: <b data-testid="gate-status">{comp.gate_preventivo?.price_status}</b></h4>
          {(comp.errori_da_preventivo || []).map((d) => (
            <div key={d.item_id} className="text-[11px] py-0.5 border-b border-border/50">
              <Badge className={`text-[9px] mr-1 ${CLS_STYLE[d.classification]}`}>{d.classification}</Badge>
              {d.label}: QuoteOS {fmt(d.quoteos_value)} vs tecnico {fmt(d.gt_value)} — {d.nota}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Overlay reale sul disegno ────────────────────────────────── */
const OV_LAYERS = [
  { key: "views", label: "Viste", color: "#7c3aed" },
  { key: "outline", label: "Contorno", color: "#0ea5e9" },
  { key: "holes", label: "Fori", color: "#dc2626" },
  { key: "slots", label: "Asole", color: "#d97706" },
  { key: "dims_linked", label: "Quote associate", color: "#16a34a" },
  { key: "dims_free", label: "Quote non associate", color: "#84cc16" },
  { key: "doubt", label: "Dubbi/da rivedere", color: "#f59e0b" },
];

const OverlayView = ({ overlay, caseId }) => {
  const [page, setPage] = useState(overlay.pages?.[0]?.page || 1);
  const [vis, setVis] = useState({ views: false, outline: true, holes: true, slots: true, dims_linked: true, dims_free: false, doubt: true });
  const [sel, setSel] = useState(null);
  const pg = useMemo(() => (overlay.pages || []).find((p) => p.page === page) || {}, [overlay, page]);
  const [x0, y0, x1, y1] = pg.page_rect || [0, 0, 595, 842];
  const holes = (overlay.holes || []).filter((h) => h.page === page);
  const slots = (overlay.slots || []).filter((s) => s.page === page);
  const dims = (overlay.dimensions || []).filter((d) => d.page === page && !d.discarded);
  const ext = overlay.lengths?.external_cut_perimeter;
  const conflictsOf = (fid) => (overlay.feature_conflicts || []).filter((c) => JSON.stringify(c).includes(fid));
  const doubtful = (x) => ["REVIEW_REQUIRED", "AMBIGUOUS"].includes(x.status) || ["REVIEW_REQUIRED", "AMBIGUOUS"].includes(x.diameter_status) || ["REVIEW_REQUIRED", "AMBIGUOUS"].includes(x.dimensions_status);

  const box = (b, color, key, data, dash, w = 1.6) => b && b.length === 4 && (
    <rect key={key} x={b[0]} y={b[1]} width={Math.max(b[2] - b[0], 2)} height={Math.max(b[3] - b[1], 2)}
      fill="none" stroke={color} strokeWidth={w} strokeDasharray={dash}
      style={{ cursor: "pointer", pointerEvents: "all" }} onClick={() => setSel(data)} />);

  return (
    <div data-testid="baseline-overlay-view">
      <div className="flex gap-3 flex-wrap items-center mb-1">
        {(overlay.pages || []).length > 1 && (
          <select className="h-6 border border-border text-[11px] bg-background" value={page} onChange={(e) => setPage(Number(e.target.value))} data-testid="overlay-page-select">
            {(overlay.pages || []).map((p) => <option key={p.page} value={p.page}>pagina {p.page}</option>)}
          </select>
        )}
        {OV_LAYERS.map((l) => (
          <label key={l.key} className="text-[11px] flex items-center gap-1" data-testid={`overlay-layer-${l.key}`}>
            <input type="checkbox" checked={!!vis[l.key]} onChange={() => setVis((v) => ({ ...v, [l.key]: !v[l.key] }))} />
            <span style={{ color: l.color }}>{l.label}</span>
          </label>
        ))}
      </div>
      <div className="relative border border-border">
        <img src={`${api.defaults.baseURL}/calibration/cases/${caseId}/page-image/${page}`} alt="disegno" className="w-full block" data-testid="overlay-page-image" />
        <svg className="absolute inset-0 w-full h-full" viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`} preserveAspectRatio="none" style={{ pointerEvents: "none" }} data-testid="overlay-svg">
          {vis.views && (pg.views || []).map((v) => box(v.bbox, "#7c3aed", v.view_id, { tipo: "vista", ...v }, "6 3"))}
          {vis.outline && ext?.bbox && box(ext.bbox, "#0ea5e9", "ext", { tipo: "contorno esterno", ...ext, page })}
          {vis.holes && holes.flatMap((h) => [box(h.bbox, "#dc2626", h.feature_id, { tipo: "foro", ...h }), ...(h.instances || []).map((ins, k) => box(ins.bbox, "#dc2626", `${h.feature_id}-i${k}`, { tipo: "foro", ...h }, "3 2", 1.1))])}
          {vis.slots && slots.map((s) => box(s.bbox, "#d97706", s.feature_id, { tipo: "asola", ...s }))}
          {vis.dims_linked && dims.filter((d) => d.linked_path).map((d) => box(d.bbox, "#16a34a", d.dim_id, { tipo: "quota associata", ...d }, "2 2", 1.1))}
          {vis.dims_free && dims.filter((d) => !d.linked_path).map((d) => box(d.bbox, "#84cc16", d.dim_id, { tipo: "quota NON associata", ...d }, "2 2", 1.1))}
          {vis.doubt && [...holes, ...slots].filter(doubtful).map((f) => box(f.bbox, "#f59e0b", `dbt-${f.feature_id}`, { tipo: "elemento dubbio", ...f }, "5 2", 2.2))}
        </svg>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1" data-testid="overlay-page-stats">
        pagina {page}: {pg.n_vector_texts} testi vettoriali · {pg.n_circles} cerchi · {pg.n_obrounds} obround · OCR {pg.ocr_used ? "sì" : "no"} · scala affidabile {pg.scale?.reliable ? "sì" : "no"}
        {(pg.excluded_circles || []).length > 0 && ` · cerchi esclusi: ${pg.excluded_circles.map((e) => e.reason).join(", ")} (coordinate non serializzate dal motore congelato)`}
      </div>
      {sel && (
        <div className="border border-border p-2 mt-2 text-xs space-y-0.5" data-testid="overlay-detail-panel">
          <div className="flex items-center gap-2">
            <b>{sel.tipo}</b>
            <span className="font-mono">{sel.feature_id || sel.dim_id || sel.view_id || sel.name}</span>
            {sel.status && <Badge className={`text-[9px] ${CLS_STYLE[sel.status] || "bg-zinc-100"}`}>{sel.status}</Badge>}
            <Button size="sm" variant="ghost" className="h-5 px-1 ml-auto" onClick={() => setSel(null)}>×</Button>
          </div>
          <div data-testid="overlay-detail-value">
            valore: {sel.tipo === "foro" ? `Ø ${fmt(sel.diameter_mm)} mm` : sel.tipo === "asola" ? `${fmt(sel.width_mm)}×${fmt(sel.length_mm)} mm` : fmt(sel.raw_text ?? sel.value)} · quantità: {fmt(sel.quantity ?? sel.qty)}
          </div>
          <div>pagina: {fmt(sel.page ?? page)} · vista: {fmt(sel.view)} · bbox: [{(sel.bbox || []).map((n) => Math.round(n)).join(", ")}]</div>
          <div>metodo: {fmt(sel.method)} · confidenza: {fmt(sel.confidence)}</div>
          {sel.evidence && <div className="text-muted-foreground">evidenza: {fmt(sel.evidence)}</div>}
          {(sel.feature_id && conflictsOf(sel.feature_id).length > 0) && (
            <div className="text-red-700" data-testid="overlay-detail-conflict">conflitto: {conflictsOf(sel.feature_id).map((c) => c.message || JSON.stringify(c)).join(" · ")}</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Pannello principale ──────────────────────────────────────── */
export const BlindBaselinePanel = ({ caseId, gtPayload }) => {
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("grezzo");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}/blind-baseline`);
      setData(r.data);
      try {
        const c = await api.get(`/calibration/cases/${caseId}/blind-comparison`, { timeout: 120000 });
        setComp(c.data);
      } catch { /* confronto non ancora possibile */ }
    } catch { setData({ missing: true }); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const freeze = async () => {
    setBusy(true);
    try {
      await api.post(`/calibration/cases/${caseId}/blind-baseline`, {}, { timeout: 300000 });
      toast.success("Estrazione cieca congelata (immutabile)");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore congelamento"); }
    setBusy(false);
  };

  if (!data) return <div className="text-sm p-2" data-testid="baseline-loading">Caricamento prova cieca…</div>;
  if (data.missing) {
    return (
      <Card data-testid="blind-baseline-panel">
        <CardHeader className="py-3"><CardTitle className="text-sm">Prova cieca QuoteOS (baseline congelata)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs mb-2">Nessuna baseline congelata per questo caso. L'estrazione usa SOLO il PDF originale e il motore congelato: la ground truth non viene mai fornita al parser.</p>
          <Button size="sm" onClick={freeze} disabled={busy} data-testid="blind-freeze-btn">
            <Snowflake size={13} className="mr-1" /> {busy ? "Estrazione in corso…" : "Esegui e congela la prova cieca"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const TABS = [["grezzo", "Output grezzo"], ["gt", "Ground truth"], ["confronto", `Confronto (${comp?.items?.length || 0})`], ["metriche", "Tabella & metriche"], ["overlay", "Overlay sul disegno"]];
  return (
    <Card data-testid="blind-baseline-panel">
      <CardHeader className="py-3">
        <CardTitle className="text-sm">Prova cieca QuoteOS — baseline congelata e immutabile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <MetaBar meta={data.meta} audit={comp?.audit_proof} />
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([k, l]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} className="h-7 text-xs" onClick={() => setTab(k)} data-testid={`blind-tab-${k}`}>{l}</Button>
          ))}
        </div>
        {tab === "grezzo" && <RawView recognized={data.recognized} expert={data.expert_summary} />}
        {tab === "gt" && <GtView gt={comp?.ground_truth_fase_a || gtPayload} />}
        {tab === "confronto" && (comp ? <CompView comp={comp} /> : <p className="text-xs text-muted-foreground" data-testid="comp-unavailable">Confronto non disponibile (FASE A non bloccata o baseline mancante).</p>)}
        {tab === "metriche" && (comp ? <MetricsView comp={comp} /> : <p className="text-xs text-muted-foreground">Confronto non disponibile.</p>)}
        {tab === "overlay" && <OverlayView overlay={data.overlay} caseId={caseId} />}
      </CardContent>
    </Card>
  );
};
